import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import {
  NotificationsActive as NotificationsActiveIcon,
  Refresh as RefreshIcon,
  Sync as SyncIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import ManagementPageLayout, { ManagementPageCard } from '../../components/layout/ManagementPageLayout';
import DataTable from '../../components/common/DataTable';
import PaymentDisputesSection from './PaymentDisputesSection';
import PaymentRefundsSection from './PaymentRefundsSection';
import {
  getAppointmentPaymentExceptions,
  getCustomerMembershipExceptions,
  getPaymentMonitoringStatus,
  getPaymentOperationsSummary,
  getPaymentWebhookEventDetail,
  getStoreSubscriptionExceptions,
  retryPaymentWebhookEvent,
  runPaymentMonitoringEvaluation,
  runStripeReconciliation,
  searchPaymentWebhookEvents,
} from '../../services/paymentOperationsService';
import { BRAND } from '../../theme/brandTokens';

const WEBHOOK_STATUS_OPTIONS = ['Failed', 'Processing', 'Processed'];
const ROWS_PER_PAGE = 25;

const MONITORING_SIGNAL_FIELDS = [
  { key: 'openFailedWebhooks', thresholdKey: 'openFailedWebhooks' },
  { key: 'recentFailedWebhooks', thresholdKey: 'recentFailedWebhooks' },
  { key: 'staleProcessingWebhooks', thresholdKey: 'staleProcessingWebhooks' },
  { key: 'recentPayoutFailures', thresholdKey: 'payoutFailures' },
  { key: 'recentPaymentFailureWebhooks', thresholdKey: 'paymentFailureWebhookEvents' },
];

const formatAlertType = (alertType) => alertType.replace(/([a-z])([A-Z])/g, '$1 $2');

const formatUtcDateTime = (value) => {
  if (!value) {
    return '—';
  }

  return new Date(value).toLocaleString();
};

const SummaryCard = ({ label, value, highlight = false }) => (
  <ManagementPageCard sx={{ p: 2.5, height: '100%' }}>
    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
      {label}
    </Typography>
    <Typography
      variant="h4"
      sx={{ fontWeight: 700, color: highlight && value > 0 ? '#b71c1c' : BRAND.text }}
    >
      {value}
    </Typography>
  </ManagementPageCard>
);

const PaymentOperationsPage = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState(0);
  const [summary, setSummary] = useState(null);
  const [loadingSummary, setLoadingSummary] = useState(true);
  const [error, setError] = useState(null);
  const [refreshToken, setRefreshToken] = useState(0);

  const [webhookStatusFilter, setWebhookStatusFilter] = useState('Failed');
  const [staleOnlyFilter, setStaleOnlyFilter] = useState(false);
  const [webhookPage, setWebhookPage] = useState(1);
  const [webhookRows, setWebhookRows] = useState([]);
  const [webhookTotal, setWebhookTotal] = useState(0);
  const [webhookLoading, setWebhookLoading] = useState(false);

  const [exceptionPage, setExceptionPage] = useState(1);
  const [exceptionRows, setExceptionRows] = useState([]);
  const [exceptionTotal, setExceptionTotal] = useState(0);
  const [exceptionLoading, setExceptionLoading] = useState(false);

  const [selectedWebhookId, setSelectedWebhookId] = useState(null);
  const [webhookDetail, setWebhookDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [retryLoading, setRetryLoading] = useState(false);
  const [reconcileLoading, setReconcileLoading] = useState(false);
  const [monitoring, setMonitoring] = useState(null);
  const [loadingMonitoring, setLoadingMonitoring] = useState(true);
  const [monitoringEvaluateLoading, setMonitoringEvaluateLoading] = useState(false);
  const [actionFeedback, setActionFeedback] = useState(null);

  const loadMonitoring = useCallback(async () => {
    setLoadingMonitoring(true);

    try {
      const result = await getPaymentMonitoringStatus();
      setMonitoring(result);
    } catch (err) {
      console.error('Failed to load payment monitoring status:', err);
      setError(err?.response?.data?.message || t('paymentOperations.error.loading'));
    } finally {
      setLoadingMonitoring(false);
    }
  }, [t]);

  const loadSummary = useCallback(async () => {
    setLoadingSummary(true);
    setError(null);

    try {
      const result = await getPaymentOperationsSummary();
      setSummary(result);
    } catch (err) {
      console.error('Failed to load payment operations summary:', err);
      setError(err?.response?.data?.message || t('paymentOperations.error.loading'));
    } finally {
      setLoadingSummary(false);
    }
  }, [t]);

  const loadWebhookEvents = useCallback(async () => {
    setWebhookLoading(true);
    setError(null);

    try {
      const result = await searchPaymentWebhookEvents({
        status: staleOnlyFilter ? undefined : webhookStatusFilter,
        staleProcessingOnly: staleOnlyFilter,
        pageIndex: webhookPage - 1,
        itemsPerPage: ROWS_PER_PAGE,
      });

      setWebhookRows(result.data);
      setWebhookTotal(result.totalItems);
    } catch (err) {
      console.error('Failed to load webhook events:', err);
      setError(err?.response?.data?.message || t('paymentOperations.error.loading'));
    } finally {
      setWebhookLoading(false);
    }
  }, [staleOnlyFilter, t, webhookPage, webhookStatusFilter]);

  const loadExceptionList = useCallback(async () => {
    setExceptionLoading(true);
    setError(null);

    try {
      const request = {
        pageIndex: exceptionPage - 1,
        itemsPerPage: ROWS_PER_PAGE,
      };

      let result;
      if (activeTab === 1) {
        result = await getAppointmentPaymentExceptions(request);
      } else if (activeTab === 2) {
        result = await getStoreSubscriptionExceptions(request);
      } else if (activeTab === 3) {
        result = await getCustomerMembershipExceptions(request);
      } else {
        return;
      }

      setExceptionRows(result.data);
      setExceptionTotal(result.totalItems);
    } catch (err) {
      console.error('Failed to load payment exceptions:', err);
      setError(err?.response?.data?.message || t('paymentOperations.error.loading'));
    } finally {
      setExceptionLoading(false);
    }
  }, [activeTab, exceptionPage, t]);

  useEffect(() => {
    loadSummary();
    loadMonitoring();
  }, [loadSummary, loadMonitoring, refreshToken]);

  useEffect(() => {
    if (activeTab === 0) {
      loadWebhookEvents();
    }
  }, [activeTab, loadWebhookEvents, refreshToken]);

  useEffect(() => {
    if (activeTab > 0 && activeTab < 4) {
      loadExceptionList();
    }
  }, [activeTab, loadExceptionList, refreshToken]);

  const openWebhookDetail = async (row) => {
    setSelectedWebhookId(row.id);
    setWebhookDetail(null);
    setDetailLoading(true);
    setActionFeedback(null);

    try {
      const detail = await getPaymentWebhookEventDetail(row.id);
      setWebhookDetail(detail);
    } catch (err) {
      console.error('Failed to load webhook detail:', err);
      setActionFeedback({
        severity: 'error',
        message: err?.response?.data?.message || t('paymentOperations.error.loading'),
      });
    } finally {
      setDetailLoading(false);
    }
  };

  const handleRetryWebhook = async () => {
    if (!selectedWebhookId) {
      return;
    }

    setRetryLoading(true);
    setActionFeedback(null);

    try {
      await retryPaymentWebhookEvent(selectedWebhookId);
      setActionFeedback({
        severity: 'success',
        message: t('paymentOperations.webhooks.retrySuccess'),
      });
      setRefreshToken((current) => current + 1);
    } catch (err) {
      console.error('Failed to retry webhook event:', err);
      setActionFeedback({
        severity: 'error',
        message: err?.response?.data?.message || t('paymentOperations.webhooks.retryError'),
      });
    } finally {
      setRetryLoading(false);
    }
  };

  const webhookColumns = useMemo(() => [
    {
      field: 'eventType',
      header: t('paymentOperations.webhooks.columns.eventType'),
      render: (row) => row.eventType,
    },
    {
      field: 'status',
      header: t('paymentOperations.webhooks.columns.status'),
      render: (row) => (
        <Chip
          size="small"
          label={row.status}
          color={row.status === 'Failed' ? 'error' : row.status === 'Processed' ? 'success' : 'warning'}
        />
      ),
    },
    {
      field: 'externalEventId',
      header: t('paymentOperations.webhooks.columns.externalEventId'),
      render: (row) => row.externalEventId,
    },
    {
      field: 'connectedAccountId',
      header: t('paymentOperations.webhooks.columns.connectedAccount'),
      render: (row) => row.connectedAccountId || t('paymentOperations.webhooks.platform'),
    },
    {
      field: 'receivedAtUtc',
      header: t('paymentOperations.webhooks.columns.receivedAt'),
      render: (row) => formatUtcDateTime(row.receivedAtUtc),
    },
    {
      field: 'lastError',
      header: t('paymentOperations.webhooks.columns.lastError'),
      render: (row) => row.lastError || '—',
    },
  ], [t]);

  const appointmentColumns = useMemo(() => [
    { field: 'storeName', header: t('paymentOperations.exceptions.columns.store'), render: (row) => row.storeName },
    { field: 'date', header: t('paymentOperations.exceptions.columns.date'), render: (row) => row.date },
    { field: 'totalPrice', header: t('paymentOperations.exceptions.columns.amount'), render: (row) => row.totalPrice },
    {
      field: 'paymentStatus',
      header: t('paymentOperations.exceptions.columns.status'),
      render: (row) => <Chip size="small" label={row.paymentStatus} color="warning" />,
    },
    {
      field: 'paymentSessionId',
      header: t('paymentOperations.exceptions.columns.sessionId'),
      render: (row) => row.paymentSessionId || '—',
    },
  ], [t]);

  const storeSubscriptionColumns = useMemo(() => [
    { field: 'storeName', header: t('paymentOperations.exceptions.columns.store'), render: (row) => row.storeName },
    {
      field: 'status',
      header: t('paymentOperations.exceptions.columns.status'),
      render: (row) => <Chip size="small" label={row.status} color="warning" />,
    },
    {
      field: 'providerSubscriptionId',
      header: t('paymentOperations.exceptions.columns.subscriptionId'),
      render: (row) => row.providerSubscriptionId || '—',
    },
    {
      field: 'endsAtUtc',
      header: t('paymentOperations.exceptions.columns.endsAt'),
      render: (row) => formatUtcDateTime(row.endsAtUtc),
    },
  ], [t]);

  const membershipColumns = useMemo(() => [
    { field: 'storeName', header: t('paymentOperations.exceptions.columns.store'), render: (row) => row.storeName },
    {
      field: 'status',
      header: t('paymentOperations.exceptions.columns.status'),
      render: (row) => <Chip size="small" label={row.status} color="warning" />,
    },
    {
      field: 'providerSubscriptionId',
      header: t('paymentOperations.exceptions.columns.subscriptionId'),
      render: (row) => row.providerSubscriptionId || '—',
    },
    {
      field: 'customerId',
      header: t('paymentOperations.exceptions.columns.customerId'),
      render: (row) => row.customerId,
    },
  ], [t]);

  const exceptionColumns = activeTab === 1
    ? appointmentColumns
    : activeTab === 2
      ? storeSubscriptionColumns
      : activeTab === 3
        ? membershipColumns
        : [];

  const handleRefresh = () => {
    setRefreshToken((current) => current + 1);
  };

  const handleEvaluateMonitoring = async () => {
    setMonitoringEvaluateLoading(true);
    setActionFeedback(null);

    try {
      const result = await runPaymentMonitoringEvaluation();
      const triggeredCount = result.triggeredAlerts?.length ?? 0;
      setActionFeedback({
        severity: triggeredCount > 0 ? 'warning' : 'success',
        message: t('paymentOperations.monitoring.evaluateSuccess', {
          triggeredCount,
          emailsSent: result.emailsSent ?? 0,
        }),
      });
      setRefreshToken((current) => current + 1);
    } catch (err) {
      console.error('Failed to evaluate payment monitoring:', err);
      setActionFeedback({
        severity: 'error',
        message: err?.response?.data?.message || t('paymentOperations.monitoring.evaluateError'),
      });
    } finally {
      setMonitoringEvaluateLoading(false);
    }
  };

  const handleReconcile = async () => {
    setReconcileLoading(true);
    setActionFeedback(null);

    try {
      const result = await runStripeReconciliation();
      setActionFeedback({
        severity: 'success',
        message: t('paymentOperations.reconciliation.successDetail', {
          appointmentsInspected: result.appointmentsInspected ?? 0,
          appointmentsMismatched: result.appointmentsMismatched ?? 0,
          appointmentsRepaired: result.appointmentsRepaired ?? 0,
          storeSubscriptionsInspected: result.storeSubscriptionsInspected ?? 0,
          storeSubscriptionsMismatched: result.storeSubscriptionsMismatched ?? 0,
          storeSubscriptionsRepaired: result.storeSubscriptionsRepaired ?? 0,
          customerMembershipsInspected: result.customerMembershipsInspected ?? 0,
          customerMembershipsMismatched: result.customerMembershipsMismatched ?? 0,
          customerMembershipsRepaired: result.customerMembershipsRepaired ?? 0,
        }),
      });
      setRefreshToken((current) => current + 1);
    } catch (err) {
      console.error('Failed to run Stripe reconciliation:', err);
      setActionFeedback({
        severity: 'error',
        message: err?.response?.data?.message || t('paymentOperations.reconciliation.error'),
      });
    } finally {
      setReconcileLoading(false);
    }
  };

  const handleTabChange = (_, newValue) => {
    setActiveTab(newValue);
    setExceptionPage(1);
    setWebhookPage(1);
    setActionFeedback(null);
  };

  return (
    <ManagementPageLayout
      pageTitle={t('paymentOperations.title')}
      pageSubtitle={t('paymentOperations.subtitle')}
      leadingContent={(
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignSelf: 'flex-start' }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            disabled={reconcileLoading || monitoringEvaluateLoading}
          >
            {t('paymentOperations.actions.refresh')}
          </Button>
          <Button
            variant="outlined"
            startIcon={monitoringEvaluateLoading ? <CircularProgress size={18} color="inherit" /> : <NotificationsActiveIcon />}
            onClick={handleEvaluateMonitoring}
            disabled={monitoringEvaluateLoading || reconcileLoading}
          >
            {monitoringEvaluateLoading
              ? t('paymentOperations.monitoring.evaluating')
              : t('paymentOperations.monitoring.evaluate')}
          </Button>
          <Button
            variant="contained"
            startIcon={reconcileLoading ? <CircularProgress size={18} color="inherit" /> : <SyncIcon />}
            onClick={handleReconcile}
            disabled={reconcileLoading || monitoringEvaluateLoading}
          >
            {reconcileLoading
              ? t('paymentOperations.actions.reconciling')
              : t('paymentOperations.actions.reconcile')}
          </Button>
        </Box>
      )}
    >
      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}

      {actionFeedback ? (
        <Alert severity={actionFeedback.severity} sx={{ mb: 2 }} onClose={() => setActionFeedback(null)}>
          {actionFeedback.message}
        </Alert>
      ) : null}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {loadingSummary ? (
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress />
            </Box>
          </Grid>
        ) : (
          <>
            <Grid item xs={12} sm={6} md={4}>
              <SummaryCard
                label={t('paymentOperations.summary.failedWebhooks')}
                value={summary?.failedWebhookEvents ?? 0}
                highlight
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <SummaryCard
                label={t('paymentOperations.summary.staleWebhooks')}
                value={summary?.staleProcessingWebhookEvents ?? 0}
                highlight
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <SummaryCard
                label={t('paymentOperations.summary.appointmentExceptions')}
                value={summary?.appointmentPaymentExceptions ?? 0}
                highlight
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <SummaryCard
                label={t('paymentOperations.summary.storeSubscriptionExceptions')}
                value={summary?.storeSubscriptionExceptions ?? 0}
                highlight
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <SummaryCard
                label={t('paymentOperations.summary.customerMembershipExceptions')}
                value={summary?.customerMembershipExceptions ?? 0}
                highlight
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <SummaryCard
                label={t('paymentOperations.summary.paymentDisputesRequiringAction')}
                value={summary?.paymentDisputesRequiringAction ?? 0}
                highlight
              />
            </Grid>
          </>
        )}
      </Grid>

      <ManagementPageCard sx={{ p: 2.5, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
              {t('paymentOperations.monitoring.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('paymentOperations.monitoring.subtitle')}
            </Typography>
          </Box>
          <Chip
            size="small"
            color={monitoring?.enabled ? 'success' : 'default'}
            label={monitoring?.enabled
              ? t('paymentOperations.monitoring.enabled')
              : t('paymentOperations.monitoring.disabled')}
          />
        </Box>

        {loadingMonitoring ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('paymentOperations.monitoring.configuration', {
                lookbackMinutes: monitoring?.lookbackMinutes ?? 0,
                cooldownMinutes: monitoring?.alertCooldownMinutes ?? 0,
                recipientCount: monitoring?.alertRecipientEmails?.length ?? 0,
              })}
            </Typography>

            <Grid container spacing={2} sx={{ mb: 2 }}>
              {MONITORING_SIGNAL_FIELDS.map(({ key, thresholdKey }) => {
                const observed = monitoring?.signals?.[key] ?? 0;
                const threshold = monitoring?.thresholds?.[thresholdKey] ?? 0;
                const breached = observed >= threshold && threshold > 0;

                return (
                  <Grid item xs={12} sm={6} md={4} key={key}>
                    <Box sx={{ p: 1.5, borderRadius: 1, bgcolor: breached ? 'error.50' : 'grey.50' }}>
                      <Typography variant="body2" color="text.secondary">
                        {t(`paymentOperations.monitoring.signals.${key}`)}
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: breached ? 'error.main' : 'text.primary' }}>
                        {observed}
                        <Typography component="span" variant="body2" color="text.secondary">
                          {` / ${threshold}`}
                        </Typography>
                      </Typography>
                    </Box>
                  </Grid>
                );
              })}
            </Grid>

            {(monitoring?.activeAlerts?.length ?? 0) > 0 ? (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  {t('paymentOperations.monitoring.activeAlerts')}
                </Typography>
                {monitoring.activeAlerts.map((alert) => (
                  <Alert key={alert.alertType} severity="warning" sx={{ mb: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {formatAlertType(alert.alertType)}
                    </Typography>
                    <Typography variant="body2">{alert.summary}</Typography>
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      {alert.recommendedAction}
                    </Typography>
                  </Alert>
                ))}
              </Box>
            ) : (
              <Alert severity="success" sx={{ mb: 2 }}>
                {t('paymentOperations.monitoring.noActiveAlerts')}
              </Alert>
            )}

            {(monitoring?.recentAlertHistory?.length ?? 0) > 0 ? (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  {t('paymentOperations.monitoring.recentHistory')}
                </Typography>
                {monitoring.recentAlertHistory.map((entry) => (
                  <Typography key={entry.alertType} variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    {`${formatAlertType(entry.alertType)} — ${formatUtcDateTime(entry.lastSentAtUtc)} (${entry.lastObservedValue})`}
                  </Typography>
                ))}
              </Box>
            ) : null}
          </>
        )}
      </ManagementPageCard>

      <ManagementPageCard sx={{ p: { xs: 1.5, md: 2 } }}>
        <Tabs value={activeTab} onChange={handleTabChange} sx={{ mb: 2 }}>
          <Tab label={t('paymentOperations.tabs.webhooks')} />
          <Tab label={t('paymentOperations.tabs.appointments')} />
          <Tab label={t('paymentOperations.tabs.storeSubscriptions')} />
          <Tab label={t('paymentOperations.tabs.customerMemberships')} />
          <Tab label={t('paymentOperations.tabs.disputes')} />
          <Tab label={t('paymentOperations.tabs.refunds')} />
        </Tabs>

        {activeTab === 4 ? (
          <PaymentDisputesSection
            refreshToken={refreshToken}
            onFeedback={setActionFeedback}
          />
        ) : null}

        {activeTab === 5 ? (
          <PaymentRefundsSection
            refreshToken={refreshToken}
            onFeedback={setActionFeedback}
          />
        ) : null}

        {activeTab === 0 ? (
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
            <FormControl size="small" sx={{ minWidth: 180 }} disabled={staleOnlyFilter}>
              <InputLabel>{t('paymentOperations.webhooks.filters.status')}</InputLabel>
              <Select
                label={t('paymentOperations.webhooks.filters.status')}
                value={webhookStatusFilter}
                onChange={(event) => {
                  setWebhookStatusFilter(event.target.value);
                  setWebhookPage(1);
                }}
              >
                {WEBHOOK_STATUS_OPTIONS.map((status) => (
                  <MenuItem key={status} value={status}>
                    {status}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button
              variant={staleOnlyFilter ? 'contained' : 'outlined'}
              onClick={() => {
                setStaleOnlyFilter((current) => !current);
                setWebhookPage(1);
              }}
            >
              {t('paymentOperations.webhooks.filters.staleOnly')}
            </Button>
          </Box>
        ) : null}

        {activeTab === 4 || activeTab === 5 ? null : (
        <DataTable
          data={activeTab === 0 ? webhookRows : exceptionRows}
          loading={activeTab === 0 ? webhookLoading : exceptionLoading}
          columns={activeTab === 0 ? webhookColumns : exceptionColumns}
          searchable={false}
          filterable={false}
          pagination
          rowsPerPage={ROWS_PER_PAGE}
          currentPage={activeTab === 0 ? webhookPage : exceptionPage}
          totalCount={activeTab === 0 ? webhookTotal : exceptionTotal}
          onPageChange={(page) => {
            if (activeTab === 0) {
              setWebhookPage(page);
            } else {
              setExceptionPage(page);
            }
          }}
          actionMenuItems={activeTab === 0
            ? [{
              label: t('paymentOperations.webhooks.viewDetails'),
              tooltip: t('paymentOperations.webhooks.viewDetails'),
              icon: <VisibilityIcon fontSize="small" />,
              onClick: (row) => openWebhookDetail(row),
            }]
            : []}
          emptyMessage={t('paymentOperations.empty')}
          noResultsMessage={t('paymentOperations.empty')}
          onRefresh={handleRefresh}
        />
        )}
      </ManagementPageCard>

      <Dialog
        open={Boolean(selectedWebhookId)}
        onClose={() => setSelectedWebhookId(null)}
        maxWidth="md"
        fullWidth
        disableScrollLock
      >
        <DialogTitle>{t('paymentOperations.webhooks.detailTitle')}</DialogTitle>
        <DialogContent dividers>
          {detailLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : webhookDetail ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Typography variant="body2"><strong>{t('paymentOperations.webhooks.columns.eventType')}:</strong> {webhookDetail.eventType}</Typography>
              <Typography variant="body2"><strong>{t('paymentOperations.webhooks.columns.status')}:</strong> {webhookDetail.status}</Typography>
              <Typography variant="body2"><strong>{t('paymentOperations.webhooks.columns.externalEventId')}:</strong> {webhookDetail.externalEventId}</Typography>
              <Typography variant="body2"><strong>{t('paymentOperations.webhooks.columns.connectedAccount')}:</strong> {webhookDetail.connectedAccountId || t('paymentOperations.webhooks.platform')}</Typography>
              <Typography variant="body2"><strong>{t('paymentOperations.webhooks.detail.correlationId')}:</strong> {webhookDetail.correlationId}</Typography>
              <Typography variant="body2"><strong>{t('paymentOperations.webhooks.detail.traceId')}:</strong> {webhookDetail.traceId}</Typography>
              <Typography variant="body2"><strong>{t('paymentOperations.webhooks.detail.deliveryCount')}:</strong> {webhookDetail.deliveryCount}</Typography>
              <Typography variant="body2"><strong>{t('paymentOperations.webhooks.columns.lastError')}:</strong> {webhookDetail.lastError || '—'}</Typography>
              <Typography variant="body2" component="pre" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', bgcolor: BRAND.background, p: 2, borderRadius: 1, maxHeight: 320, overflow: 'auto' }}>
                {webhookDetail.rawPayload}
              </Typography>
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedWebhookId(null)}>
            {t('common.close')}
          </Button>
          <Button
            variant="contained"
            disabled={retryLoading || detailLoading || !webhookDetail}
            onClick={handleRetryWebhook}
          >
            {retryLoading ? t('paymentOperations.webhooks.retrying') : t('paymentOperations.webhooks.retry')}
          </Button>
        </DialogActions>
      </Dialog>
    </ManagementPageLayout>
  );
};

export default PaymentOperationsPage;
